
import { Component, Vue, Watch, Prop } from "vue-property-decorator";
import { API_ROUTES } from "@/utils/API";
import { API_TCaseCategory } from "@/models/case_profiles";
import { SMQuestionnaire, SMUser } from "@/store";

const HOME_PAGE_NAME_EMPLOYER = "EmployerCompliance";
const HOME_PAGE_NAME_EMPLOYER_EMPLOYEE = "EmployerEmployeesTemplateSummary";
const HOME_PAGE_NAME_EMPLOYEE = "EmployeeHome";
const HOME_PAGE_NAME_EXPERT = "CompaniesSummary";

const TYPE_COMPLIANCE_PROFILE = "complianceProfile";
const TYPE_COMPLIANCE_PROFILE_EMPLOYEE = "complianceProfileEmployee";
const TYPE_EXPERT_COMPANIES_PROFILE = "expertCompaniesProfile";

const DIALOG_TYPE_ASK = "ask";
const DIALOG_TYPE_ATTENTION = "attention";
const DIALOGS = {
  ask: DIALOG_TYPE_ASK,
  attention: DIALOG_TYPE_ATTENTION,
};

@Component({
  components: {
    Loader: () => import("@/components/Loader/Loader.vue"),
    ProfileTemplate: () => import("@/modules/Compliance/Profile/ProfileTemplate.vue"),
  },
})
export default class ComplianceProfile extends Vue {
  @Prop({ type: String, required: true }) readonly type!: string;
  @Prop({ type: Boolean, default: true }) readonly canEdit!: boolean;

  categories: API_TCaseCategory[] = [] as API_TCaseCategory[];

  stepId = 0;

  categoryId = 0;

  dialog = {
    editDialog: false,
    notCompletedTaskDialog: false,
  };

  dialogs = DIALOGS;

  mounted() {
    this.fetchCaseProfile();

    this.$root.$on("update-employee-level-data", this.fetchCaseProfile);
  }

  get profileId(): string {
    return this.$route.params.tabTypeId;
  }

  @Watch("profileId")
  profileIdChanged() {
    this.fetchCaseProfile();
  }

  get dataIsEdited() {
    return SMQuestionnaire.lastUpdatedStep;
  }

  @Watch("dataIsEdited", { deep: true, immediate: true })
  dataIsEditedChanged() {
    this.fetchCaseProfile();
  }

  init = false;

  get baseUrl() {
    const { type } = this;

    if (!type) return "";

    if (type === TYPE_COMPLIANCE_PROFILE) return API_ROUTES.COMPLIANCE_PROFILE;

    const { id, company_id } = this.$route.params;

    if (id && type === TYPE_COMPLIANCE_PROFILE_EMPLOYEE)
      return API_ROUTES.COMPLIANCE_PROFILE_EMPLOYEE(id);

    if (company_id && type === TYPE_EXPERT_COMPANIES_PROFILE)
      return API_ROUTES.EXPERT_COMPANIES_PROFILE(company_id);

    return "";
  }

  get editCheckUrl(): string {
    const { id, company_id } = this.$route.params;

    return API_ROUTES.COMPLIANCE_EDIT_CHECK({
      targetUserId: id,
      target_user_id: id,
      company_id,
    });
  }

  editStepUrl(stepId: number): string {
    const { id, company_id } = this.$route.params;

    return API_ROUTES.COMPLIANCE_EDIT_STEP(stepId, {
      target_user_id: id,
      company_id,
    });
  }

  async fetchCaseProfile() {
    if (!this.baseUrl) return (this.init = true);

    try {
      const { data } = await this.axios.get(this.baseUrl, {
        params: { alias: this.$route.meta.alias },
      });

      if (!data) return;

      this.categories = data.categories;
    } finally {
      this.init = true;
    }
  }

  async onEdit(payload: { stepId: number; isHistory: boolean; categoryId: number }) {
    const { stepId, isHistory, categoryId } = payload;

    this.stepId = stepId;

    if (!isHistory) return await this.editCheck(stepId);

    this.categoryId = categoryId;

    this.dialog.editDialog = true;
  }

  async editCheck(stepId: number) {
    try {
      const id = stepId || this.stepId;

      if (!id) return;

      const { data } = await this.axios.get(this.editCheckUrl);

      if (!data) return (this.dialog.notCompletedTaskDialog = true);

      return await this.confirmQuestionEditing(id);
    } catch (e) {
      console.log("editCheck error:", e);
    }
  }

  async confirmQuestionEditing(stepId: number) {
    try {
      if (!stepId) return;

      const { data } = await this.axios.get(this.editStepUrl(stepId));

      if (!data) return;

      this.closeEditDialog();

      await SMQuestionnaire.openQuestionnaire(data.ID);
    } catch (e) {
      console.log("confirmQuestionEditing error:", e);
    }
  }

  async addHistoricalData() {
    try {
      if (!this.categoryId) return;

      const { data } = await this.axios.get(
        API_ROUTES.COMPLIANCE_CATEGORY_HISTORICAL_ADD(this.categoryId)
      );

      if (!data) return;

      this.closeEditDialog();

      await SMQuestionnaire.openQuestionnaire(data.ID);
    } catch (e) {
      console.log("addHistoricalData error:", e);
    }
  }

  closeNotCompletedTaskDialog() {
    this.dialog.notCompletedTaskDialog = false;
  }

  closeEditDialog() {
    this.dialog.editDialog = false;
  }

  get homePageName() {
    const { type } = this;

    if (!type) return SMUser.basePath;

    if (SMUser.isEmployer && type === TYPE_COMPLIANCE_PROFILE_EMPLOYEE)
      return HOME_PAGE_NAME_EMPLOYER_EMPLOYEE;

    if (type === TYPE_EXPERT_COMPANIES_PROFILE) return HOME_PAGE_NAME_EXPERT;

    if (type !== TYPE_COMPLIANCE_PROFILE) return "";

    if (SMUser.isEmployer) return HOME_PAGE_NAME_EMPLOYER;

    if (SMUser.isEmployee) return HOME_PAGE_NAME_EMPLOYEE;

    return SMUser.basePath;
  }

  async viewNotCompletedTasks() {
    try {
      await this.$router.push({ name: this.homePageName });
    } finally {
      this.closeNotCompletedTaskDialog();
    }
  }

  get isEditable() {
    if (SMUser.isExpert) return true;

    return !SMUser.isComplianceBlocked && this.canEdit;
  }
}
